import React, { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '@/context/ThemeContext';
import { useDbMode } from '@/context/DbModeContext';
import { Sun, Moon, LogOut, User, ChevronDown, FlaskConical, Database, Presentation, Trophy } from 'lucide-react';

// Helper pour gérer les URLs d'images
const getImageUrl = (imageValue, API) => {
  if (!imageValue) return null;
  if (imageValue.startsWith('data:')) return imageValue;
  if (imageValue.startsWith('http')) return imageValue;
  return `${API}/files/${imageValue}`;
};

const UserDropdown = ({ user, onLogout, API, isCollapsed = false, additionalItems = null }) => {
  const navigate = useNavigate();
  const { theme, toggleTheme } = useTheme();
  const { dbMode, setDbMode, allowSwitch, demoMode, setDemoMode, allowDemo } = useDbMode();
  const showDevSwitch = allowSwitch && user?.is_developer && user?.is_admin;
  const showDemoSwitch = allowDemo && user?.is_demo;
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
  const dropdownRef = useRef(null);
  const triggerRef = useRef(null);

  // Calculer la position du dropdown quand il s'ouvre
  const calculatePosition = useCallback(() => {
    if (triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + 8,
        left: rect.left,
      });
    }
  }, []);

  // Fermer le dropdown quand on clique en dehors
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRef.current && 
        !dropdownRef.current.contains(event.target) &&
        triggerRef.current &&
        !triggerRef.current.contains(event.target)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Fermer avec Escape
  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, []);

  const handleToggleTheme = useCallback((e) => {
    e.stopPropagation();
    e.preventDefault();
    toggleTheme();
  }, [toggleTheme]);

  const handleLogout = useCallback(() => {
    setIsOpen(false);
    onLogout();
  }, [onLogout]);

  const handleTriggerClick = useCallback(() => {
    const newState = !isOpen;
    if (newState) {
      // Calculer la position avant d'ouvrir
      calculatePosition();
    }
    setIsOpen(newState);
  }, [isOpen, calculatePosition]);

  return (
    <div className="relative">
      {/* Trigger */}
      <button
        ref={triggerRef}
        onClick={handleTriggerClick}
        data-testid="user-profile-dropdown"
        className={`w-full flex items-center gap-3 p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors ${isCollapsed ? 'justify-center' : ''}`}
      >
        {/* Photo utilisateur */}
        <div className="relative flex-shrink-0">
          {user?.photo ? (
            <img
              src={getImageUrl(user.photo, API)}
              alt={user.display_name}
              className="w-10 h-10 rounded-full object-cover border-2 border-gray-200 dark:border-gray-600 relative"
            />
          ) : (
            <div className="w-10 h-10 rounded-full bg-[#f76f4b] flex items-center justify-center relative">
              <User size={20} className="text-white" />
            </div>
          )}
          {/* Indicateur en ligne */}
          <div className="absolute bottom-0 right-0 w-3 h-3 bg-green-500 border-2 border-white dark:border-gray-800 rounded-full" style={{ zIndex: 2 }}></div>
        </div>
        
        {/* Nom et info */}
        {!isCollapsed && (
          <>
            <div className="flex-1 text-left overflow-hidden">
              <p className="font-semibold text-sm text-[#080808] dark:text-white truncate">
                {user?.display_name || `${user?.first_name} ${user?.last_name}`}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                {user?.position || 'Utilisateur'}
              </p>
            </div>
            {/* Flèche indicateur */}
            <ChevronDown 
              size={16} 
              className={`text-gray-400 flex-shrink-0 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} 
            />
          </>
        )}
      </button>

      {/* Dropdown Content - utilise un portail pour sortir du stacking context de la sidebar */}
      {isOpen && createPortal(
        <div
          ref={dropdownRef}
          data-testid="user-profile-dropdown-menu"
          style={{
            position: 'fixed',
            top: dropdownPosition.top,
            left: dropdownPosition.left,
            zIndex: 10000,
          }}
          className={`w-72 p-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg`}
        >
          {/* Toggle Mode Sombre/Clair */}
          <div 
            data-testid="theme-toggle"
            className="mb-2 p-3 bg-gray-100 dark:bg-gray-700 rounded-lg cursor-pointer hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
            onClick={handleToggleTheme}
          >
            <div className="flex items-center justify-between gap-4 w-full">
              <div className="flex items-center gap-2 flex-shrink-0">
                {theme === 'light' ? (
                  <Sun size={18} className="text-yellow-500" />
                ) : (
                  <Moon size={18} className="text-blue-400" />
                )}
                <span className="text-sm font-medium dark:text-white whitespace-nowrap">
                  {theme === 'light' ? 'Mode clair' : 'Mode sombre'}
                </span>
              </div>
              <div
                className={`relative w-14 h-7 rounded-full transition-colors duration-300 flex-shrink-0 ${
                  theme === 'dark' ? 'bg-blue-600' : 'bg-gray-300'
                }`}
              >
                <span
                  className={`absolute top-1 w-5 h-5 rounded-full bg-white shadow-md transition-transform duration-300 ${
                    theme === 'dark' ? 'translate-x-8' : 'translate-x-1'
                  }`}
                />
              </div>
            </div>
          </div>

          {/* Toggle Dev/Prod DB — visible uniquement pour les admins développeurs en localhost */}
          {showDevSwitch && (
            <div
              className="mb-2 p-3 bg-gray-100 dark:bg-gray-700 rounded-lg cursor-pointer hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
              onClick={() => setDbMode(dbMode === 'dev' ? 'prod' : 'dev')}
            >
              <div className="flex items-center justify-between gap-4 w-full">
                <div className="flex items-center gap-2 flex-shrink-0">
                  {dbMode === 'dev' ? (
                    <FlaskConical size={18} className="text-orange-500" />
                  ) : (
                    <Database size={18} className="text-gray-500 dark:text-gray-400" />
                  )}
                  <span className="text-sm font-medium dark:text-white whitespace-nowrap">
                    {dbMode === 'dev' ? 'Mode Dev' : 'Mode Prod'}
                  </span>
                </div>
                <div
                  className={`relative w-14 h-7 rounded-full transition-colors duration-300 flex-shrink-0 ${
                    dbMode === 'dev' ? 'bg-orange-500' : 'bg-gray-300'
                  }`}
                >
                  <span
                    className={`absolute top-1 w-5 h-5 rounded-full bg-white shadow-md transition-transform duration-300 ${
                      dbMode === 'dev' ? 'translate-x-8' : 'translate-x-1'
                    }`}
                  />
                </div>
              </div>
            </div>
          )}

          {/* Toggle Mode Démo — visible pour les utilisateurs is_demo sur la prod */}
          {showDemoSwitch && (
            <div
              className="mb-2 p-3 bg-gray-100 dark:bg-gray-700 rounded-lg cursor-pointer hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
              onClick={() => setDemoMode(demoMode === 'demo' ? 'real' : 'demo')}
            >
              <div className="flex items-center justify-between gap-4 w-full">
                <div className="flex items-center gap-2 flex-shrink-0">
                  {demoMode === 'demo' ? (
                    <Presentation size={18} className="text-purple-500" />
                  ) : (
                    <Database size={18} className="text-gray-500 dark:text-gray-400" />
                  )}
                  <span className="text-sm font-medium dark:text-white whitespace-nowrap">
                    {demoMode === 'demo' ? 'Mode Démo' : 'Mode Réel'}
                  </span>
                </div>
                <div
                  className={`relative w-14 h-7 rounded-full transition-colors duration-300 flex-shrink-0 ${
                    demoMode === 'demo' ? 'bg-purple-500' : 'bg-gray-300'
                  }`}
                >
                  <span
                    className={`absolute top-1 w-5 h-5 rounded-full bg-white shadow-md transition-transform duration-300 ${
                      demoMode === 'demo' ? 'translate-x-8' : 'translate-x-1'
                    }`}
                  />
                </div>
              </div>
            </div>
          )}

          {/* Éléments additionnels (ex: Mode Maintenance pour admin) */}
          {additionalItems}

          {/* Séparateur */}
          <div className="my-2 h-px bg-gray-200 dark:bg-gray-700" />

          <button
            type="button"
            onClick={() => {
              setIsOpen(false);
              navigate('/vitrine');
            }}
            data-testid="user-vitrine-link"
            className="w-full flex items-center gap-2 px-2 py-1.5 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg cursor-pointer transition-colors"
          >
            <Trophy size={16} className="text-[#f76f4b]" />
            Ma vitrine
          </button>

          {/* Séparateur */}
          <div className="my-2 h-px bg-gray-200 dark:bg-gray-700" />
          
          {/* Déconnexion */}
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-2 px-2 py-1.5 text-sm text-red-600 dark:text-red-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg cursor-pointer transition-colors"
          >
            <LogOut size={16} />
            Déconnexion
          </button>
        </div>,
        document.body
      )}
    </div>
  );
};

export default UserDropdown;
